import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useCurrentSession } from '../stores/session';
import { createSocketClient, type ConnectionState, type SocketClient } from './socket';

// Mounted once inside the shell, which itself never re-renders (CLAUDE.md §5a
// rule 6). One connection for the life of the session.

const SocketContext = createContext<SocketClient | null>(null);

export function SocketProvider({ children }: { children: ReactNode }): ReactNode {
  const queryClient = useQueryClient();
  const userId = useCurrentSession()?.id;

  // One client per query client. Created in state rather than a memo so React
  // cannot discard and recreate it, which would leave an orphaned socket.
  const [client] = useState(() => createSocketClient(queryClient));

  useEffect(() => () => client.disconnect(), [client]);

  // The notification bell's room. S8 authorises membership at join time against
  // the same permission engine as REST; until then this is a no-op on the wire.
  // Tied to the session, not to page load: the gateway refuses anyone without
  // one, and signing in without a reload must still start live updates.
  useEffect(() => {
    if (!userId) {
      client.stop();
      return;
    }
    client.joinRoom(`user:${userId}`);
    client.start();
  }, [client, userId]);

  return <SocketContext.Provider value={client}>{children}</SocketContext.Provider>;
}

function useSocketClient(): SocketClient | null {
  return useContext(SocketContext);
}

/**
 * Joins a record's room while the screen showing it is open — a job's pipeline, so a
 * move on a colleague's board reaches this one (S16, ADR 0003). Membership is checked
 * by the server at join time; the pushes carry keys only (#12).
 */
export function useRoom(room: string | null): void {
  const client = useSocketClient();
  useEffect(() => {
    if (!client || room === null) return undefined;
    client.joinRoom(room);
    return () => client.leaveRoom(room);
  }, [client, room]);
}

/** The socket's own view of whether it is connected. */
export function useSocketState(): ConnectionState {
  const client = useSocketClient();

  return useSyncExternalStore(
    (onChange) => (client ? client.subscribe(onChange) : () => undefined),
    () => client?.getState() ?? 'absent',
    () => 'absent' as const,
  );
}

export type NetworkStatus = 'online' | 'reconnecting' | 'offline';

/**
 * What SYS-06 renders from.
 *
 * Deliberately socket-first, not `navigator.onLine`-first: a socket can be dead
 * while the network is perfectly fine — the server restarted, a proxy dropped the
 * upgrade, the gateway lost Redis. `navigator.onLine` would report everything well
 * while live updates had silently stopped, which is exactly the situation the
 * banner exists to surface.
 *
 * Two rules on top of that:
 *
 * - The browser being offline outranks everything, because in that state actions
 *   will fail outright rather than merely arriving late.
 * - `absent` — no gateway has ever answered — reads as online. Until S8 there is
 *   no server half, and a permanent "reconnecting" banner for six slices would
 *   train everyone to ignore the one warning that matters. A connection that was
 *   established and then lost is a different thing, and does raise the banner.
 */
export function useNetworkStatus(): NetworkStatus {
  const socketState = useSocketState();
  const isBrowserOnline = useSyncExternalStore(
    (onChange) => {
      window.addEventListener('online', onChange);
      window.addEventListener('offline', onChange);
      return () => {
        window.removeEventListener('online', onChange);
        window.removeEventListener('offline', onChange);
      };
    },
    () => navigator.onLine,
    () => true,
  );

  return useMemo(() => {
    if (!isBrowserOnline) return 'offline';
    if (socketState === 'connected' || socketState === 'absent') return 'online';
    return 'reconnecting';
  }, [isBrowserOnline, socketState]);
}
